import type { Formatters } from '../types';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 1,
});

const numberFormatter = new Intl.NumberFormat('en-US');

/** `percent` expects a fraction (0.07), not a whole percentage (7). */
export const fmt: Formatters = {
  currency: (n) => currencyFormatter.format(n),
  percent: (n) => percentFormatter.format(n),
  number: (n) => numberFormatter.format(n),
};

/** Strips thousands separators/currency symbols before parsing user input. */
export function parseNumeric(raw: string): number | null {
  const cleaned = raw.replace(/[^0-9.-]/g, '');
  if (cleaned === '' || cleaned === '-') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

export function clamp(n: number, min?: number, max?: number): number {
  let v = n;
  if (min !== undefined) v = Math.max(min, v);
  if (max !== undefined) v = Math.min(max, v);
  return v;
}

/**
 * Rounds to the decimal precision of `step` (e.g. step 0.1 -> 1 decimal
 * place). Found 2026-09-22 (Brad): repeatedly adding a fractional step
 * (interestRatePct's step is 0.1) drifts on raw floating-point addition —
 * 3 -> 3.1 -> 3.2000000000000002 -> ... -> 3.5000000000000004 after a few
 * clicks, the classic 0.1 + 0.2 problem. Snapping back to the step's own
 * precision after every increment/decrement clears the drift instead of
 * letting it compound.
 */
export function roundToStep(n: number, step: number): number {
  const decimals = (String(step).split('.')[1] || '').length;
  const factor = 10 ** decimals;
  return Math.round(n * factor) / factor;
}

/**
 * Next snap value in `direction` from `current`: the nearest listed value
 * strictly above (up) or below (down). A value typed between two snap points
 * (e.g. 12 years with snaps ...10, 15...) steps to its neighbors (15 / 10)
 * rather than jumping to the list's ends. Stays put at either end.
 */
export function snapStep(snaps: number[], current: number | null, direction: 1 | -1): number {
  const sorted = [...snaps].sort((a, b) => a - b);
  if (current === null) return direction === 1 ? sorted[0] : sorted[sorted.length - 1];
  if (direction === 1) {
    const next = sorted.find((v) => v > current);
    return next ?? sorted[sorted.length - 1];
  }
  const prev = [...sorted].reverse().find((v) => v < current);
  return prev ?? sorted[0];
}
